import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../ui/utils';

type KpiTrendProps = {
    value: number | null;
    label: string;
};

function formatTrendPct(value: number): string {
    return Math.abs(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

function KpiTrend({ value, label }: KpiTrendProps) {
    if (value == null) {
        return (
            <span className="nx-kpi-trend nx-kpi-trend--neutral">
                <em>{label}</em>
            </span>
        );
    }

    const positive = value >= 0;

    return (
        <span className={cn('nx-kpi-trend', positive ? 'nx-kpi-trend--up' : 'nx-kpi-trend--down')}>
            <span aria-hidden>{positive ? '▲' : '▼'}</span>
            {formatTrendPct(value)}%
            <em>{label}</em>
        </span>
    );
}

type KpiSparklineProps = {
    data: number[];
    color?: string;
    className?: string;
};

/** Mini sparkline SVG — série opcional; se vazia, não renderiza. */
function KpiSparkline({ data, color = '#5c6bc0', className }: KpiSparklineProps) {
    const points = data.filter((n) => Number.isFinite(n));
    if (points.length < 2) return null;

    const w = 72;
    const h = 28;
    const pad = 2;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;

    const coords = points.map((v, i) => {
        const x = pad + (i / (points.length - 1)) * (w - pad * 2);
        const y = h - pad - ((v - min) / span) * (h - pad * 2);
        return `${x},${y}`;
    });

    const line = coords.join(' ');
    const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;

    return (
        <svg
            className={cn('nx-kpi-spark', className)}
            width={w}
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            aria-hidden
        >
            <polygon points={area} fill={color} opacity={0.12} />
            <polyline
                points={line}
                fill="none"
                stroke={color}
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

type KpiCardProps = {
    title: string;
    value: string;
    trend?: number | null;
    trendLabel?: string;
    icon?: LucideIcon;
    iconBg?: string;
    iconColor?: string;
    /** Série leve para sparkline (já disponível no painel ou decorativa). */
    sparkline?: number[];
    sparklineColor?: string;
    className?: string;
    children?: ReactNode;
    onClick?: () => void;
    active?: boolean;
};

function KpiCard({
    title,
    value,
    trend = null,
    trendLabel,
    icon: Icon,
    iconBg = 'var(--nx-accent-soft)',
    iconColor = 'var(--nx-accent)',
    sparkline,
    sparklineColor,
    className,
    children,
    onClick,
    active = false,
}: KpiCardProps) {
    return (
        <article
            className={cn('nx-kpi-card', active && 'nx-kpi-card--active', className)}
            onClick={onClick}
        >
            <div className="nx-kpi-header">
                <span className="nx-kpi-title">{title}</span>
                {Icon ? (
                    <div
                        className="nx-kpi-icon"
                        style={{
                            background: iconBg,
                            color: iconColor,
                        }}
                    >
                        <Icon size={16} strokeWidth={2.2} />
                    </div>
                ) : null}
            </div>

            <div className="nx-kpi-body">
                <strong className="nx-kpi-value">{value}</strong>
                <div className="nx-kpi-foot">
                    {children ??
                        (trendLabel != null ? (
                            <KpiTrend value={trend} label={trendLabel} />
                        ) : (
                            <span />
                        ))}
                    {sparkline && sparkline.length >= 2 ? (
                        <KpiSparkline data={sparkline} color={sparklineColor} />
                    ) : null}
                </div>
            </div>
        </article>
    );
}

export { KpiCard, KpiSparkline, KpiTrend };

export type { KpiCardProps, KpiSparklineProps, KpiTrendProps };
